import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ROLES } from "@/config/roles";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";

export default function MasterDataUsersPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    nama_lengkap: "",
    password: "",
    jenis: "",
    master_fakultas_id: "",
  });

  const { data: fakultasData, isLoading: isLoadingFakultas } = useQuery({
    queryKey: ["master-fakultas-all"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/master-fakultas?page=1&limit=1000`,
      );
    },
  });

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users-list"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(`${baseUrl}/api/v1/admin`);
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (payload: typeof formData) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const finalPayload = {
        ...payload,
        master_fakultas_id: payload.master_fakultas_id
          ? parseInt(payload.master_fakultas_id, 10)
          : 0,
      };
      return apiClient<any>(`${baseUrl}/api/v1/auth/register`, {
        method: "POST",
        body: JSON.stringify(finalPayload),
      });
    },
    onSuccess: () => {
      toast.success("Berhasil mendaftarkan user baru");
      queryClient.invalidateQueries({ queryKey: ["users-list"] });
      setOpen(false);
      setFormData({
        name: "",
        nama_lengkap: "",
        password: "changeme",
        jenis: "",
        master_fakultas_id: "",
      });
    },
    onError: (error: any) => {
      toast.error(error?.message || "Gagal mendaftarkan user");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    registerMutation.mutate(formData);
  };

  const updatePasswordMutation = useMutation({
    mutationFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/auth/update/${selectedUser.name}`,
        {
          method: "PUT",
          body: JSON.stringify({ password: newPassword }),
        },
      );
    },
    onSuccess: () => {
      toast.success("Berhasil mengubah password");
      setOpenPassword(false);
      setNewPassword("changeme");
      setSelectedUser(null);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Gagal mengubah password");
    },
  });

  const handleUpdatePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    updatePasswordMutation.mutate();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          Master Data Users
        </h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              Register User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-106.25">
            <DialogHeader>
              <DialogTitle>Register User Baru</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Username / Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nama_lengkap">Nama Lengkap</Label>
                <Input
                  id="nama_lengkap"
                  value={formData.nama_lengkap}
                  onChange={(e) =>
                    setFormData({ ...formData, nama_lengkap: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jenis">Jenis / Role</Label>
                <Select
                  value={formData.jenis}
                  onValueChange={(val) =>
                    setFormData({ ...formData, jenis: val })
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Role" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLES).map(([, value]) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="master_fakultas_id">Fakultas</Label>
                <Select
                  value={formData.master_fakultas_id || "none"}
                  onValueChange={(val) =>
                    setFormData({
                      ...formData,
                      master_fakultas_id: val === "none" ? "" : val,
                    })
                  }
                >
                  <SelectTrigger disabled={isLoadingFakultas}>
                    <SelectValue placeholder="Pilih Fakultas (Opsional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak Ada</SelectItem>
                    {fakultasData?.datas?.map((f: any) => (
                      <SelectItem key={f.kode_fakultas} value={f.kode_fakultas}>
                        {f.nama_fakultas}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Batal
                </Button>
                <Button type="submit" disabled={registerMutation.isPending}>
                  {registerMutation.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Register
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-12 text-center border-r">No</TableHead>
                <TableHead className="border-r">Username</TableHead>
                <TableHead className="border-r">Nama Lengkap</TableHead>
                <TableHead className="border-r">Role</TableHead>
                <TableHead className="border-r">ID Fakultas</TableHead>
                <TableHead className="w-32 text-center">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingUsers ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                  </TableCell>
                </TableRow>
              ) : usersData?.datas?.length > 0 ? (
                usersData.datas.map((user: any, idx: number) => (
                  <TableRow
                    key={user.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell className="text-center border-r text-slate-500">
                      {idx + 1}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900 border-r">
                      {user.name}
                    </TableCell>
                    <TableCell className="border-r">
                      {user.nama_lengkap}
                    </TableCell>
                    <TableCell className="border-r">{user.jenis}</TableCell>
                    <TableCell className="border-r">
                      {user.master_fakultas_id === 0
                        ? "-"
                        : user.master_fakultas_id}
                    </TableCell>
                    <TableCell className="text-center p-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs font-medium"
                        onClick={() => {
                          setSelectedUser(user);
                          setOpenPassword(true);
                        }}
                      >
                        Ganti Password
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="h-24 text-center text-slate-500"
                  >
                    Tidak ada data user.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={openPassword} onOpenChange={setOpenPassword}>
        <DialogContent className="sm:max-w-106.25">
          <DialogHeader>
            <DialogTitle>Ganti Password - {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePassword} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Password Baru</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenPassword(false)}
              >
                Batal
              </Button>
              <Button type="submit" disabled={updatePasswordMutation.isPending}>
                {updatePasswordMutation.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
